import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { IonicModule, ModalController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { User } from 'src/app/entities/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { FlashcardService } from 'src/app/services/flashcard.service';
import { Flashcard } from 'src/app/entities/wordcard.model';
import { Subscription } from 'rxjs';
import { Timestamp } from '@angular/fire/firestore';
import { LANGUAGE_MAPPINGS } from '../settings/language-mapping';
import { FlashCardModalComponent } from 'src/app/components/flash-card-modal/flash-card-modal.component';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-study',
  standalone: true,
  imports: [IonicModule, CommonModule, RouterModule, TranslateModule],
  templateUrl: './study.page.html',
  styleUrls: ['./study.page.scss'],
})
export class StudyPage implements OnInit, OnDestroy, AfterViewInit {
  public user: User | null = null;
  public flashCards: Flashcard[] = [];
  private allCards: Flashcard[] = [];
  private flashcardSub?: Subscription;
  private userSub?: Subscription;
  public sortDescending = true; // Default: newest to oldest
  public targetLanguage: string = ''; // Default language

  // Add these properties for counts
  public newCount = 0;
  public youngCount = 0;
  public matureCount = 0;

  constructor(private authService: AuthService, 
    private flashCardService: FlashcardService,
    private translateService: TranslateService,
    private router: Router,
    private modalController: ModalController,
    private userService: UserService
  ) { }

  ngOnInit() {
    document.title = 'Study';
    this.userSub = this.authService.currentUser$.subscribe(user => {
      this.user = user;
      this.flashcardSub?.unsubscribe();
      if (user && user.targetLanguage && !this.userService.touring) {
         // Translate target language name
        this.translateTargetLanguage();
        this.flashcardSub = this.flashCardService
          .initializeStudyDeck(user.uid, user.targetLanguage)
          .subscribe(cards => {
            this.allCards = cards.slice();
            this.sortAndSetCards();
            this.updateCardCounts();
          });
      } else {
        this.flashCards = [];
        this.allCards = [];
        this.updateCardCounts();
      }
    });
  }

  ngAfterViewInit() {
    // Ensure the page is fully loaded before checking for tour
    setTimeout(() => {
      if (this.userService.touring) {
         this.translateTargetLanguage();
          // Load the default card for the tour
          this.loadDefaultCard();
          this.newCount = 1; // Default card counts as new
      }
    }, 0);
  }

  translateTargetLanguage() {
    if (this.user && this.user.targetLanguage) {
      let targetLang = this.user.targetLanguage;
      let translationArray = LANGUAGE_MAPPINGS[this.user.nativeLanguage];
      this.targetLanguage = translationArray.find(lang => lang.code === targetLang)?.name || '';
    } else {
      this.targetLanguage = '';
    }
  }

  toggleSortOrder() {
    this.sortDescending = !this.sortDescending;
    this.sortAndSetCards();
  }

  private sortAndSetCards() {
    this.flashCards = this.allCards.slice().sort((a, b) => {
      const aTime = this.getTimestampMillis(a.createdAt);
      const bTime = this.getTimestampMillis(b.createdAt);
      return this.sortDescending ? bTime - aTime : aTime - bTime;
    });
    this.updateCardCounts();
  }

  private updateCardCounts() {
    this.newCount = this.flashCards.filter(card => this.getCardStatus(card) === 'HARD').length;
    this.youngCount = this.flashCards.filter(card => this.getCardStatus(card) === 'DIFFICULT').length;
    this.matureCount = this.flashCards.filter(card => this.getCardStatus(card) === 'EASY').length;
  }

  ngOnDestroy() {
    this.flashcardSub?.unsubscribe();
    this.userSub?.unsubscribe();
  }

  getCardStatus(card: Flashcard): string {
    if (card.repetitions === 0) {
      return "HARD";
    } else if (card.interval < 21) {
      return "DIFFICULT";
    } else {
      return "EASY";
    }
  }

  translateStatus(status: string): string {
    switch (status) {
      case 'HARD':
        return this.translateService.instant('STUDY.HARD');
      case 'DIFFICULT':
        return this.translateService.instant('STUDY.DIFFICULT');
      case 'EASY':
        return this.translateService.instant('STUDY.EASY');
      default:
        return this.translateService.instant('STUDY.UNKNOWN');
    }
  }

  getWordStatusString(card: Flashcard): string {
    return this.translateService.instant('STUDY.WORD_STATUS', {status: this.getCardStatus(card)});
  }

  getWordsStudying() {
    let number = this.flashCards.length;
    if (number === 0) {
      return this.translateService.instant('STUDY.NO_WORDS');
    } else {
      return this.translateService.instant('STUDY.TOTAL_STUDYING', { count: number });
    }
  }

  private getTimestampMillis(ts: any): number {
    // Firestore Timestamp or JS Date
    if (!ts) return 0;
    if (typeof ts === 'number') return ts;
    if (ts instanceof Date) return ts.getTime();
    if (ts.seconds) return ts.seconds * 1000;
    return 0;
  }

  beginLesson() {
    // Create a study session with the current flashcards
    this.flashCardService.createStudySession(this.flashCards);
    // Navigate to the study session page
    let next_card = this.flashCardService.goToNextCard();
    if (next_card) {
      this.router.navigate(['/study', next_card.id]);
    }
  }

  async openFlashCard(flashcard: Flashcard) {
    // Navigate to the flashcard detail page
        // Open the modal as a sheet modal (bottom sheet)
    const modal = await this.modalController.create({
      component: FlashCardModalComponent,
      componentProps: {
       flashcard: flashcard,
      },
      breakpoints: [0, 0.5, 0.9],
      initialBreakpoint: 0.5,
      handle: true,
      cssClass: 'flash-card-modal'
    });
    await modal.present();
  }

  loadDefaultCard() {
    this.flashCardService.generateDefaultCard(this.user?.targetLanguage || 'en').then(defaultCard => {
      this.flashCards = [defaultCard];
      this.allCards = [defaultCard];
    }).catch(err => {
      console.error('Error generating default card:', err);
    }
    );
  }
}